import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    Stack,
    Typography,
} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import usePaymentContext from '../hook/usePaymentContext'
import styles from '../paymentDialog.style'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { useCallback, useMemo } from 'react'
import { PaymentIntegrationProcessStatus } from '../Models/Enum'
import { MapPaymentIcon } from 'components/MapPaymentIcon/mapPaymentIcon'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { useTranslation } from 'react-i18next'
import { useConfirmDialog } from 'store/actions/ui.action'
import { LoadingButton } from '@mui/lab'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'

export function EvoPaymentDialog({
    openDialog,
    onClose,
    amount,
    onPaid,
}: {
    openDialog: boolean
    onClose: () => void
    amount: number
    onPaid: () => void
}) {
    const { t } = useTranslation()
    const {
        paymentIntegrationProcessStatus: paymentProcessStatus,
        checkEvoPaymentStatus,
        evoPaymentTransactionId,
        isCheckingEvoPaymentStatus,
        errorMessage,
    } = usePaymentContext()

    const { isMobile } = useScreenSize()

    const checStatusButton = useMemo(() => {
        return (
            <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                    if (checkEvoPaymentStatus) {
                        checkEvoPaymentStatus(evoPaymentTransactionId || '')
                    }
                }}
                disabled={
                    evoPaymentTransactionId ? isCheckingEvoPaymentStatus : true
                }
            >
                {isCheckingEvoPaymentStatus
                    ? t('finish.message.checking')
                    : t('finish.button.check_status')}
            </Button>
        )
    }, [
        checkEvoPaymentStatus,
        evoPaymentTransactionId,
        isCheckingEvoPaymentStatus,
        t,
    ])

    const onPayBtnClick = () => {
        onPaid()
    }
    const { showWarningDialog } = useConfirmDialog()
    const handleOnClose = useCallback(() => {
        if (paymentProcessStatus === PaymentIntegrationProcessStatus.LOADING) {
            showWarningDialog(
                t('finish.message.close_evo_dialog_warning'),
                (confirm) => {
                    if (confirm) onClose()
                },
                'Warning',
            )
        } else onClose()
    }, [onClose, paymentProcessStatus, showWarningDialog, t])

    return (
        <Dialog
            open={openDialog}
            onClose={handleOnClose}
            fullWidth
            maxWidth="sm"
        >
            <AppDialogTitle text={t('finish.payment_type.evo_payment')} />
            <CloseDialogButton
                dialogName="evo payment dialog"
                onClick={handleOnClose}
            />
            <DialogContent style={{ padding: 5 }}>
                <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                >
                    {paymentProcessStatus ===
                        PaymentIntegrationProcessStatus.IDLE && (
                        <>
                            <Box pt={2}>
                                <MapPaymentIcon
                                    paymentTypeId={PAYMENT_TYPE_ID.EVO_PAYMENT}
                                    size={120}
                                />
                            </Box>

                            <Typography color={'primary'}>
                                Amount:{' '}
                                <MonetaryFormat isOnlyString value={amount} />
                            </Typography>
                        </>
                    )}
                    {paymentProcessStatus ===
                        PaymentIntegrationProcessStatus.LOADING && (
                        <>
                            <Box pt={2}>
                                <MapPaymentIcon
                                    paymentTypeId={PAYMENT_TYPE_ID.EVO_PAYMENT}
                                    size={120}
                                />
                            </Box>
                            <CircularProgress />
                            <Typography color={'primary'}>
                                {'Payment is ' +
                                    (isCheckingEvoPaymentStatus
                                        ? 'checking...'
                                        : 'processing...')}
                            </Typography>

                            <Grid
                                container
                                spacing={1}
                                direction={isMobile ? 'column' : 'row'}
                                alignItems={'center'}
                                justifyContent={'center'}
                            >
                                <Grid item>
                                    <Typography
                                        pr={1}
                                        sx={{
                                            fontSize: 'small',
                                            color: 'grey',
                                        }}
                                    >
                                        {t(
                                            'finish.message.manual_check_status',
                                        ) + ':'}
                                    </Typography>
                                </Grid>
                                <Grid item>{checStatusButton}</Grid>
                            </Grid>
                        </>
                    )}
                    {paymentProcessStatus ===
                        PaymentIntegrationProcessStatus.FAIL && (
                        <>
                            <CancelIcon sx={{ color: 'red', fontSize: 70 }} />
                            <Typography>
                                {t('finish.message.payment_declined')}
                            </Typography>
                            <Typography
                                sx={{ fontSize: 'small', color: 'red' }}
                            >
                                {errorMessage}
                            </Typography>
                        </>
                    )}
                    <Stack
                        p={2}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        width="100%"
                    >
                        <Button
                            sx={{
                                ...styles.confirmButton,
                                width: 150,
                            }}
                            variant="outlined"
                            color="primary"
                            onClick={handleOnClose}
                        >
                            {t('dialog.button.close')}
                        </Button>
                        <LoadingButton
                            loading={
                                paymentProcessStatus ===
                                PaymentIntegrationProcessStatus.LOADING
                            }
                            sx={{
                                ...styles.confirmButton,
                                width: 150,
                            }}
                            color="primary"
                            onClick={onPayBtnClick}
                            disabled={
                                paymentProcessStatus ===
                                PaymentIntegrationProcessStatus.LOADING
                            }
                        >
                            {paymentProcessStatus ===
                            PaymentIntegrationProcessStatus.FAIL
                                ? t('finish.button.retry')
                                : t('finish.button.pay')}
                        </LoadingButton>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}
